import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Overline } from "@/components/Reveal";
import { BRAND } from "@/lib/constants";

const SECTIONS = [
  {
    h: "1. Introduction",
    p: [
      `${BRAND.name} ("we", "us", "our") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose and safeguard your information when you visit our website, place an order, or interact with us (collectively, the "Platform").`,
      `This Policy is published in accordance with the Information Technology Act, 2000, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and applicable data-protection laws of India. By using the Platform, you consent to the practices described here.`,
    ],
  },
  {
    h: "2. Information We Collect",
    list: [
      `Identity & Contact Data: name, email address, phone number and delivery/billing address.`,
      `Order Data: products purchased, order value, coupons used and order history.`,
      `Payment Data: processed securely by our payment partner (Razorpay). We do not store your complete card, UPI or bank details on our servers.`,
      `Technical Data: IP address, browser type, device information and pages visited.`,
      `Usage & Cookie Data: how you browse and interact with the Platform (see Section 7).`,
      `Communications: messages, feedback and support queries you send us.`,
    ],
  },
  {
    h: "3. How We Use Your Information",
    list: [
      `To process, fulfil and deliver your orders and send order confirmations and updates.`,
      `To manage payments, refunds and cancellations.`,
      `To provide customer support and respond to your queries.`,
      `To improve our products, website, services and user experience.`,
      `To send offers, promotions and marketing communications (only where permitted; you may opt out anytime).`,
      `To detect, prevent and address fraud, security issues and technical problems.`,
      `To comply with legal obligations and enforce our Terms & Conditions.`,
    ],
  },
  {
    h: "4. Legal Basis for Processing",
    p: [
      `We process your personal data on the basis of your consent, the performance of a contract (fulfilling your order), our legitimate business interests (such as improving our services and preventing fraud), and to comply with legal obligations.`,
    ],
  },
  {
    h: "5. Sharing & Disclosure",
    p: [
      `We do not sell your personal data. We share information only with trusted third parties to the extent necessary, including:`,
    ],
    list: [
      `Payment gateways (e.g., Razorpay) to process transactions securely.`,
      `Logistics and courier partners to deliver your orders.`,
      `Service providers who help us operate the Platform (hosting, analytics, communications).`,
      `Government authorities or law-enforcement agencies where required by law.`,
    ],
  },
  {
    h: "6. Data Retention",
    p: [
      `We retain your personal data only for as long as necessary to fulfil the purposes described in this Policy, including to satisfy legal, accounting, tax or reporting requirements. When data is no longer required, we securely delete or anonymise it.`,
    ],
  },
  {
    h: "7. Cookies & Tracking",
    p: [
      `The Platform uses cookies and similar technologies to keep your cart and session, remember preferences, analyse traffic and improve performance. You can control or disable cookies through your browser settings; however, some features of the Platform may not function correctly without them.`,
    ],
  },
  {
    h: "8. Data Security",
    p: [
      `We implement reasonable technical and organisational security measures — including encryption in transit and secure payment processing — to protect your personal data against unauthorised access, alteration, disclosure or destruction. However, no method of transmission over the Internet is completely secure, and we cannot guarantee absolute security.`,
    ],
  },
  {
    h: "9. Your Rights",
    list: [
      `Access: request a copy of the personal data we hold about you.`,
      `Correction: request that we correct inaccurate or incomplete data.`,
      `Deletion: request deletion of your data, subject to legal retention requirements.`,
      `Withdraw Consent: opt out of marketing communications at any time.`,
      `Grievance: raise concerns with our Grievance Officer (see Section 12).`,
    ],
  },
  {
    h: "10. Children's Privacy",
    p: [
      `The Platform is not directed at children under 18 years of age, and we do not knowingly collect personal data from minors. If you believe a minor has provided us with personal data, please contact us and we will take steps to remove it.`,
    ],
  },
  {
    h: "11. Third-Party Links",
    p: [
      `The Platform may contain links to third-party websites and social-media platforms. This Privacy Policy does not apply to those external sites. We encourage you to review the privacy policies of any third-party services you visit.`,
    ],
  },
  {
    h: "12. Grievance Officer & Contact",
    p: [
      `In accordance with the Information Technology Act, 2000 and rules made thereunder, if you have any questions, concerns or grievances about this Privacy Policy or the handling of your personal data, please contact us:`,
    ],
    contact: true,
  },
  {
    h: "13. Changes to This Policy",
    p: [
      `We may update this Privacy Policy from time to time. The revised version will be effective upon posting on the Platform with an updated "Last updated" date. Please review this page periodically to stay informed about how we protect your information.`,
    ],
  },
];

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy Policy — Dignity Food Kitchen";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Privacy Policy for Dignity Food Kitchen — how we collect, use, share and protect your personal data.");
  }, []);

  return (
    <div className="min-h-screen bg-black pt-28">
      <div className="mx-auto max-w-4xl px-5 pb-24 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-1 py-4 font-oswald text-xs uppercase tracking-[0.15em] text-white/50 hover:text-gold" data-testid="privacy-back">
          <ChevronLeft size={16} /> Back to Home
        </Link>

        <div className="mt-4">
          <Overline>Legal</Overline>
        </div>
        <h1 className="mt-3 font-display text-5xl leading-none tracking-tight text-cream sm:text-7xl">
          PRIVACY <span className="text-gold-gradient">POLICY</span>
        </h1>
        <p className="mt-4 text-sm text-white/40">Last updated: June 2026</p>

        <div className="mt-12 space-y-10" data-testid="privacy-content">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="font-oswald text-xl uppercase tracking-wide text-gold sm:text-2xl">{s.h}</h2>
              {s.p && s.p.map((para, i) => (
                <p key={i} className="mt-3 leading-relaxed text-white/65">{para}</p>
              ))}
              {s.list && (
                <ul className="mt-3 space-y-2">
                  {s.list.map((li, i) => (
                    <li key={i} className="flex gap-2 leading-relaxed text-white/65">
                      <span className="mt-1 text-gold">•</span><span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.contact && (
                <div className="mt-4 rounded-2xl border border-gold/25 bg-forest-deep p-5 text-sm text-white/70">
                  <div className="font-oswald uppercase tracking-wide text-cream">{BRAND.name}</div>
                  <div className="mt-2">Phone: {BRAND.phone}</div>
                  <div>Support: {BRAND.emails.support}</div>
                  <div>Email: {BRAND.emails.ceo}</div>
                  <div>Address: {BRAND.address}</div>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
